import { useEffect, useState } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import LabDataTable from "./LabDataTable";
import { LabDataItem } from "../Types/LabData";

function LabDataTableSetup({ labData = [] }: { labData: LabDataItem[] }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState<keyof LabDataItem>('dateSampled');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [density, setDensity] = useState<'compact' | 'standard'>('compact');
    const [visibleColumns, setVisibleColumns] = useState<string[]>([
        'dateSampled', 'fryColor0', 'fryColor1', 'fryColor2', 'fryColor3',
        'fryColor4', 'sugarEnds', 'sfaColor', 'undesirable', 'internal', 'external',
        'green', 'noDefects'
    ]);
    const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (labData.length > 0) {
            // Get all possible column keys from the columns definition
            const allColumnIds = columns.map(col => col.id);

            // Find columns that have at least one non-null value
            const columnsWithValues = allColumnIds.filter(columnId => {
                // Always include dateSampled
                if (columnId === 'dateSampled') return true;

                // Check if column has any non-null values
                return labData.some(row =>
                    row[columnId] !== null &&
                    row[columnId] !== undefined &&
                    row[columnId] !== ''
                );
            });

            // Update visible columns
            if (isSmallScreen) {
                // On small screens, start with a minimal set of columns
                const minimalColumns = ['dateSampled', 'fryColor0', 'sfaColor'];
                setVisibleColumns(columnsWithValues.filter(id => minimalColumns.includes(id)));
            } else {
                setVisibleColumns(columnsWithValues);
            }
        }
    }, [labData, isSmallScreen]); // Add isSmallScreen to dependency array


    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property: keyof LabDataItem) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const createSortHandler = (property: keyof LabDataItem) => () => {
        handleRequestSort(property);
    };

    // Sorting function
    const sortedData = [...labData].sort((a, b) => {
        if (order === 'asc') {
            return a[orderBy] < b[orderBy] ? -1 : 1;
        } else {
            return b[orderBy] < a[orderBy] ? -1 : 1;
        }
    });

    // Paginate data
    const paginatedData = sortedData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Define the table columns and their display labels
    const columns = [
        { id: 'dateSampled', label: 'Date Sampled', minWidth: 90, alwaysShow: true },
        { id: 'fryColor0', label: 'Fry Color 0', minWidth: 60, group: 'Fry Colors' },
        { id: 'fryColor1', label: 'Fry Color 1', minWidth: 60, group: 'Fry Colors' },
        { id: 'fryColor2', label: 'Fry Color 2', minWidth: 60, group: 'Fry Colors' },
        { id: 'fryColor3', label: 'Fry Color 3', minWidth: 60, group: 'Fry Colors' },
        { id: 'fryColor4', label: 'Fry Color 4', minWidth: 60, group: 'Fry Colors' },
        { id: 'sugarEnds', label: 'Sugar Ends', minWidth: 90 },
        { id: 'otherFryNotes', label: 'Fry Notes', minWidth: 100 },
        { id: 'sfaColor', label: 'SFA Color', minWidth: 80 },
        { id: 'undesirable', label: 'Undesirable (g)', minWidth: 70, tooltip: 'Undesirable' },
        { id: 'internal', label: 'Internal (g)', minWidth: 60, tooltip: 'Internal' },
        { id: 'external', label: 'External (g)', minWidth: 60, tooltip: 'External' },
        { id: 'green', label: 'Green (g)', minWidth: 60 },
        { id: 'noDefects', label: 'No Defects (g)', minWidth: 70, tooltip: 'No Defects' },
        { id: 'otherChipNotes', label: 'Chip Notes', minWidth: 100 }
    ];

    // For smaller screens, show fewer columns
    const responsiveColumns = columns.filter(col => visibleColumns.includes(col.id));

    const handleOpenColumnMenu = (event: React.MouseEvent<HTMLElement>) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleCloseColumnMenu = () => {
        setColumnMenuAnchor(null);
    };

    const handleToggleColumn = (columnId: string) => {
        setVisibleColumns(prev => {
            // Always keep dateSampled visible
            if (columnId === 'dateSampled') return prev;

            return prev.includes(columnId)
                ? prev.filter(id => id !== columnId)
                : [...prev, columnId];
        });
    };

    const handleToggleDensity = () => {
        setDensity(prev => prev === 'compact' ? 'standard' : 'compact');
    };
    return (<>
        <LabDataTable
            labData={labData}
            handleToggleDensity={handleToggleDensity}
            handleOpenColumnMenu={handleOpenColumnMenu}
            handleCloseColumnMenu={handleCloseColumnMenu}
            handleToggleColumn={handleToggleColumn}
            handleChangePage={handleChangePage}
            responsiveColumns={responsiveColumns}
            createSortHandler={createSortHandler}
            paginatedData={paginatedData}
            isSmallScreen={isSmallScreen}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            columnMenuAnchor={columnMenuAnchor}
            density={density}
            columns={columns}
            visibleColumns={visibleColumns}
            page={page}
            rowsPerPage={rowsPerPage}
            order={order}
            orderBy={orderBy}
        />
    </>)
}

export default LabDataTableSetup;

